import { omit } from 'lodash';
import { BaseLogger } from './BaseLogger';
import { LOGGER_DEBOUNCE_TIME } from './constants';
import { AppMetadata, LoggerType, TrackingData } from './models';
import { AdobeLogger, AFTLogger, GenStatsLogger } from './services';
import { LoggerUtil } from './utils/LoggerUtils';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- Utility class with only static methods, used as singleton pattern
export class Logger {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- Private constructor prevents instantiation
  private constructor() {}

  private static loggerInstances: { name: LoggerType; logger: BaseLogger }[] = [];
  private static appMetadata: AppMetadata;
  // keep track of debounced events
  private static _debounceMap = new Map();
  // debounced track event function- which will debounced wrt parameters
  private static _debouncedTrackEvent = (trackingData: TrackingData, loggerInstances?: LoggerType[]) => {
    const id = LoggerUtil.getHashCode(JSON.stringify(omit(trackingData, ['srcEvent', 'srcElement'])));
    const lastCallTime = Logger._debounceMap.get(id) || 0;
    const currentTime = new Date().getTime();

    if (currentTime - lastCallTime >= LOGGER_DEBOUNCE_TIME) {
      Logger.loggerInstances.forEach((loggerObj) => {
        if (loggerInstances?.length && !loggerInstances.includes(loggerObj.name)) {
          return;
        }
        loggerObj.logger.trackEvent(trackingData);
      });

      Logger._debounceMap.set(id, currentTime);
    }

    setTimeout(() => {
      Logger._debounceMap.delete(id);
    }, LOGGER_DEBOUNCE_TIME);
  };

  /**
   * Register logger instances
   * @param loggerNames - Array of logger names to register
   * @param appMetadata - App metadata which contains logger config as well as app level info
   */
  public static async register(loggerNames: LoggerType[], appMetadata: AppMetadata) {
    Logger.appMetadata = { ...Logger.appMetadata, ...appMetadata };
    for (const name of loggerNames) {
      let logger: BaseLogger;
      switch (name) {
        case LoggerType.AFTLogger:
          logger = new AFTLogger();
          break;
        case LoggerType.GenStatsLogger:
          logger = new GenStatsLogger();
          break;
        case LoggerType.AdobeLogger:
          logger = new AdobeLogger();
          break;
        default:
          throw new Error('Logger not found');
      }
      if (Logger.loggerInstances.findIndex((loggerObj) => loggerObj.name === name) === -1) {
        Logger.loggerInstances.push({ name, logger });
      }
      await logger.initLogger(Logger.appMetadata);
    }
  }

  /**
   * Unregister logger instances
   * @param loggerNames - Array of logger names to unregister
   */
  public static unregister(loggerNames: LoggerType[]) {
    loggerNames.forEach((name) => {
      const loggerIndex = Logger.loggerInstances.findIndex((loggerObj) => loggerObj.name === name);
      if (loggerIndex > -1) {
        Logger.loggerInstances.splice(loggerIndex, 1);
      }
    });
  }

  /**
   * Unregister all logger instances
   */
  public static unregisterAll() {
    Logger.loggerInstances = [];
    document.removeEventListener('click', Logger.declarativeTrackingHandler);
  }

  /**
   * Track event
   * @param trackingData - Tracking data to be sent
   * @param loggerInstances - Logger instances override to which event should be sent
   */
  public static trackEvent(trackingData: TrackingData, loggerInstances?: LoggerType[]) {
    Logger._debouncedTrackEvent(trackingData, loggerInstances);
  }

  // get logger instance
  public static getLogger(name: LoggerType): BaseLogger | undefined {
    return Logger.loggerInstances.find((loggerInstance) => loggerInstance.name === name)?.logger;
  }

  /**
   * Flush all queued events immediately with keepalive enabled.
   * Call before programmatic redirects/navigations to prevent event loss.
   */
  public static flush(): void {
    Logger.loggerInstances.forEach((loggerObj) => {
      loggerObj.logger.flush?.();
    });
  }

  /**
   * Reset session id
   */
  public static resetSessionId(): void {
    Logger.loggerInstances.forEach((loggerObj) => {
      loggerObj.logger.resetSessionId?.();
    });
  }

  /**
   * Event handler function to capture tracking data via custom attribute
   * @param e - event
   */
  public static declarativeTrackingHandler(e: MouseEvent): void {
    const trackingData = LoggerUtil.getTrackingData(e, Logger.appMetadata.interactiveElementSelectors);
    if (trackingData) {
      Logger._debouncedTrackEvent(trackingData);
    }
  }

  /**
   * Apply app metadata update to all logger instances
   * @param appMetadata AppMetadata to set
   */
  public static setAppMetadata(appMetadata: AppMetadata): void {
    Logger.loggerInstances.forEach((loggerObj) => {
      loggerObj.logger.setAppMetadata?.(appMetadata);
    });
  }

  /**
   * Get app metadata
   */
  public static getAppMetadata(): AppMetadata {
    return Logger.appMetadata;
  }
}
